// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngCarousel, { XngCarouselStyles } from "@synd/App/Components/XngCarousel";
import { CustomAppState } from "@synd/App/Store/Reducers";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@synd/Overrides/Components/ProductCarouselProduct";
import { SLIDES_PER_VIEW_POINTS } from "@synd/XDL/constants";
import { generateSlidesPerViewMap } from "@synd/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import loadPurchasedProducts from "@insite/client-framework/Store/Components/PurchasedProducts/Handlers/LoadPurchasedProducts";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import PurchasedProductCard from "@insite/content-library/Components/PurchasedProductCard";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import stubFalse from "lodash/stubFalse";
import * as React from "react";
import { Fragment, useEffect, useRef } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import styled, { css } from "styled-components";
import { CarouselCardType } from "@synd/XDL/types";

const enum fields {
    title = "title",
    titlePosition = "titlePosition",
    purchaseType = "purchaseType",
    showBrand = "showBrand",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAvailability = "showAvailability",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    // XNG-Changes
    view = "view",
    showImage = "showImage",
    showTitle = "showTitle",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.titlePosition]: string;
        [fields.purchaseType]: string;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        // XNG-Changes
        [fields.view]: CarouselCardType;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
    };
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const session = getSession(state);
    const isAuthenticated = (session.isAuthenticated || session.rememberMe) && !session.isGuest;
    return {
        products: getProductsForProductInfoList(state, ownProps.id),
        canViewPurchasedProducts: isAuthenticated,
        // XNG-Changes
        isMobileScreen: state.xCommon.isMobileScreen,
    };
};

const mapDispatchToProps = {
    loadPurchasedProducts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PurchasedProductsStyles {
    titleWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    headerContainer?: GridContainerProps;
    itemHeaderGridItem?: GridItemProps;
    itemHeaderText?: TypographyPresentationProps;
    priceHeaderGridItem?: GridItemProps;
    priceHeaderText?: TypographyPresentationProps;
    addToCartHeaderGridItem?: GridItemProps;
    addToCartHeaderText?: TypographyPresentationProps;
    // XNG-Change
    carouselProductStyles?: ProductCarouselProductStyles;
    carouselProductCondensedStyles?: ProductCarouselProductStyles;
    miniCardStyles?: ProductCarouselProductStyles;
    swiperStyles?: XngCarouselStyles;
    condensedSwiperStyles?: XngCarouselStyles;
    miniCardWrapper?: InjectableCss;
    miniCardProductWrapper?: InjectableCss;
}

export const purchasedProductsStyles: PurchasedProductsStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    headerContainer: {
        gap: 0,
        css: css`
            padding: 10px;
            background-color: ${getColor("common.accent")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    itemHeaderGridItem: { width: [0, 0, 6, 6, 6] },
    itemHeaderText: { weight: "bold" },
    priceHeaderGridItem: {
        width: [0, 0, 3, 3, 3],
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        padding-right: 35px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    priceHeaderText: { weight: "bold" },
    addToCartHeaderGridItem: {
        width: [0, 0, 3, 3, 3],
        css: css`
            justify-content: flex-end;
        `,
    },
    addToCartHeaderText: { weight: "bold" },
};

const styles = purchasedProductsStyles;

const getJustifyContent = (position: string) => {
    if (position === "left") {
        return "flex-start";
    }
    if (position === "right") {
        return "flex-end";
    }
    return "center";
};

const TitleWrapper = styled.div<{ position: string }>`
    display: flex;
    justify-content: ${({ position }) => getJustifyContent(position)};
`;

const PurchasedProducts = ({
    id,
    fields,
    products,
    canViewPurchasedProducts,
    loadPurchasedProducts,
    zone,
    // XNG-Changes
    isMobileScreen,
}: Props) => {
    React.useEffect(() => {
        if (canViewPurchasedProducts) {
            loadPurchasedProducts({ widgetId: id, purchaseType: fields.purchaseType });
        }
    }, [fields.purchaseType]);

    // <XNG-Changes>

    const dispatch = useDispatch();
    const zoneInit = useRef(false);

    useEffect(() => {
        if (products && products?.length > 0 && zone.includes("tab") && !zoneInit.current) {
            dispatch({
                type: "xTabs/EnableZone",
                data: {
                    zone,
                    isEnabled: true, // we need to show this tab if we are in tab context
                },
            });
            zoneInit.current = true;
        } else if (!products?.length && zone.includes("tab")) {
            dispatch({
                type: "xTabs/EnableZone",
                data: {
                    zone,
                    isEnabled: false,
                },
            });
        }
    }, [products]);
    const breakpoints = generateSlidesPerViewMap({
        [SLIDES_PER_VIEW_POINTS.xs]: 1,
        [SLIDES_PER_VIEW_POINTS.sm]: 1.8,
        [SLIDES_PER_VIEW_POINTS.md]: 2.8,
        [SLIDES_PER_VIEW_POINTS.lg]: 3,
    });

    const condensedBreakPoints = generateSlidesPerViewMap({
        [SLIDES_PER_VIEW_POINTS.xs]: 1,
        [SLIDES_PER_VIEW_POINTS.sm]: 1.8,
        [SLIDES_PER_VIEW_POINTS.md]: 3.8,
        [SLIDES_PER_VIEW_POINTS.lg]: 6,
    });

    const swiperProps =
        fields.view === "condensed"
            ? { slideMaxWidth: "204px", extendedStyles: styles.condensedSwiperStyles }
            : { slideMaxWidth: "317px", extendedStyles: styles.swiperStyles };

    const carouselCardStyles =
        fields.view === "condensed"
            ? styles.carouselProductCondensedStyles
            : fields.view === "miniCard"
              ? styles.miniCardStyles
              : styles.carouselProductStyles;
    // </XNG-Changes>

    if (!canViewPurchasedProducts || !products || products.length === 0) {
        return null;
    }

    if (fields.view === "table") {
        if (!isMobileScreen) {
            return (
                <>
                    <TitleWrapper {...styles.titleWrapper} position={fields.titlePosition}>
                        <Typography {...styles.titleText}>{fields.title}</Typography>
                    </TitleWrapper>
                    <GridContainer {...styles.headerContainer}>
                        <GridItem {...styles.itemHeaderGridItem}>
                            <Typography {...styles.itemHeaderText}>{translate("Item")}</Typography>
                        </GridItem>
                        {fields.showPrice && (
                            <GridItem {...styles.priceHeaderGridItem}>
                                <Typography {...styles.priceHeaderText}>{translate("Price/Unit")}</Typography>
                            </GridItem>
                        )}
                        {(fields.showAddToCart || fields.showAddToList) && (
                            <GridItem {...styles.addToCartHeaderGridItem}>
                                <Typography {...styles.addToCartHeaderText}>{translate("Add to Cart")}</Typography>
                            </GridItem>
                        )}
                    </GridContainer>
                    {products.map(product => (
                        <PurchasedProductCard
                            key={product.id}
                            product={product}
                            widgetId={id}
                            showBrand={fields.showBrand}
                            showPartNumbers={fields.showPartNumbers}
                            showPrice={fields.showPrice}
                            showAvailability={fields.showAvailability}
                            showAddToCart={fields.showAddToCart}
                            showAddToList={fields.showAddToList}
                        />
                    ))}
                </>
            );
        }

        return (
            <StyledWrapper {...styles.miniCardWrapper}>
                {products.map(product => (
                    <StyledWrapper {...styles.miniCardProductWrapper} key={product.id}>
                        <ProductCarouselProduct
                            carouselId={id}
                            product={product}
                            // recommendationId={getProductRecommendationId(product)}
                            showImage={true}
                            showBrand={fields.showBrand}
                            showTitle={true}
                            showPartNumbers={fields.showPartNumbers}
                            showPrice={fields.showPrice}
                            showAddToCart={fields.showAddToCart}
                            showAddToList={fields.showAddToList}
                            // XNG-Changes
                            showImageBadges={fields.showImageBadges}
                            maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                            // XNG-Changes
                            showTextBadges={fields.showTextBadges}
                            maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                            extendedStyles={styles.miniCardStyles}
                            {...{
                                view: "miniCard",
                                showAvailability: fields.showAvailability,
                            }}
                        />
                    </StyledWrapper>
                ))}
            </StyledWrapper>
        );
    }

    return (
        <>
            <TitleWrapper {...styles.titleWrapper} position={fields.titlePosition}>
                <Typography {...styles.titleText}>{fields.title}</Typography>
            </TitleWrapper>
            <XngCarousel navigation={true} spaceBetween={24} scrollbar={true} {...swiperProps}>
                {products.map(product => (
                    <Fragment key={product.id}>
                        <ProductCarouselProduct
                            carouselId={id}
                            product={product}
                            showImage={fields.showImage}
                            showBrand={fields.showBrand}
                            showTitle={fields.showTitle}
                            showPartNumbers={fields.showPartNumbers}
                            showPrice={fields.showPrice}
                            showAddToCart={fields.showAddToCart && fields.view !== "condensed"}
                            showAddToList={fields.showAddToList && fields.view !== "condensed"}
                            showImageBadges={fields.showImageBadges}
                            maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                            showTextBadges={fields.showTextBadges}
                            maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                            extendedStyles={carouselCardStyles}
                            {...{
                                view: fields.view,
                                showAvailability: fields.showAvailability,
                            }}
                        />
                    </Fragment>
                ))}
            </XngCarousel>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PurchasedProducts),
    definition: {
        group: "Common",
        icon: "list-ul",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.titlePosition,
                displayName: "Title Position",
                editorTemplate: "DropDownField",
                options: [
                    { value: "center", displayName: "Center" },
                    { value: "left", displayName: "Left" },
                    { value: "right", displayName: "Right" },
                ],
                defaultValue: "center",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.purchaseType,
                displayName: "Purchase Type",
                editorTemplate: "DropDownField",
                options: [
                    { value: "recently", displayName: "Recently Purchased" },
                    { value: "frequently", displayName: "Frequently Purchased" },
                ],
                defaultValue: "recently",
                fieldType: "General",
                sortOrder: 2,
            },
            // <XNG-Changes>
            {
                name: fields.view,
                displayName: "View",
                editorTemplate: "RadioButtonsField",
                defaultValue: "grid",
                horizontal: true,
                fieldType: "General",
                sortOrder: 2,
                options: [
                    {
                        displayName: "Table",
                        value: "table",
                    },
                    {
                        displayName: "Grid",
                        value: "grid",
                    },
                    {
                        displayName: "Condensed",
                        value: "miniCard",
                    },
                    {
                        displayName: "Thumbnail",
                        value: "condensed",
                    },
                ],
            },

            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "Contextual",
                isVisible: widget => widget.fields.view !== "table",
                sortOrder: 3,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => true,
                fieldType: "Contextual",
                isVisible: widget => widget.fields.view !== "table",
                sortOrder: 4,
            },
            // </XNG-Changes>
            {
                name: fields.showBrand,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showAvailability,
                displayName: "Availability",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            // <XNG-Changes>
            {
                name: fields.showImageBadges,
                displayName: "Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 11,
                isVisible: widget => widget.fields.view !== "table",
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 12,
            },
            {
                name: fields.showTextBadges,
                displayName: "Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
                sortOrder: 13,
                isVisible: widget => widget.fields.view !== "table",
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 14,
            },
            // </XNG-Changes>
        ],
    },
};

export default widgetModule;
